import {
  APPOINTMENT_STATUS_OF_ROW,
  ROW_STATUS_OF_APPOINTMENT_STATUS,
  storedClock,
  type Appointment,
  type AppointmentCounts,
  type AppointmentFilterOptions as ApiFilterOptions,
  type AppointmentFilters,
  type AppointmentListQuery,
  type AppointmentPage,
  type AppointmentSortKey,
  type AppointmentStatus,
  type StoredWorkSnapshot,
} from "@shyne/shared/api";
import type { uuid } from "@/interfaces";
import {
  appointment_status,
  individual_payment_status,
  subs_payment_status,
  type AppointmentMetaData,
  type PreviousWorkSnapshot,
} from "@/models/appointment.model.ts";
import type { AppointmentFilterOptions, AppointmentJoin } from "@/repositories/appointments.repository.ts";

const DOMAIN_STATUS: Record<appointment_status, AppointmentStatus> = APPOINTMENT_STATUS_OF_ROW;

const ROW_STATUS = ROW_STATUS_OF_APPOINTMENT_STATUS as Record<AppointmentStatus, appointment_status>;

export const rowStatusOf = (status: AppointmentStatus): appointment_status => ROW_STATUS[status];

const ROW_STATUSES: ReadonlySet<string> = new Set(Object.keys(DOMAIN_STATUS));

export const isRowStatus = (status: string): status is appointment_status => ROW_STATUSES.has(status);

export type RowStatusCounts = { total: number; byStatus: Record<appointment_status, number> };

export const FINISHED_OR_COMPLETED: readonly appointment_status[] = [appointment_status.done, appointment_status.completed];

export const finishedOrCompletedCount = (byStatus: Record<appointment_status, number>): number =>
  FINISHED_OR_COMPLETED.reduce((sum, status) => sum + byStatus[status], 0);

export const toRowStatusCounts = ({ total, byStatus }: Partial<AppointmentCounts>): RowStatusCounts => ({
  total: total ?? 0,
  byStatus: Object.fromEntries(Object.entries(DOMAIN_STATUS).map(([row, domain]) => [row, byStatus?.[domain] ?? 0])) as Record<
    appointment_status,
    number
  >,
});

const SORT_KEY: Record<string, AppointmentSortKey> = {
  created_at: "createdAt",
  date: "date",
  status: "status",
  id: "id",
  "property.name": "property",
  "properties.name": "property",
  unit_number: "unit",
  service: "service",
  "services.name": "service",
  vendor: "vendor",
  assigned_user: "worker",
  final_price: "price",
};

export function toApiSortKey(sortKey: string): AppointmentSortKey {
  const apiSortKey = SORT_KEY[sortKey];
  if (apiSortKey === undefined) throw new Error(`The appointment list cannot sort by "${sortKey}"`);
  return apiSortKey;
}

export type AppointmentRowFilters = {
  status?: appointment_status | "" | null;
  statuses?: readonly appointment_status[];
  property?: uuid | null;
  serviceParent?: string | null;
  vendorName?: string | null;
  assignee?: string | null;
  assigneeUser?: string | null;
  search?: string | null;
  searchVendor?: boolean;
  dateFrom?: string | null;
  dateTo?: string | null;
};

const unlessEmpty = (value: string | null | undefined) => (value ? value : undefined);

export function toApiFilters(filters: AppointmentRowFilters, actingOrganization: uuid | null): Partial<AppointmentFilters> {
  const query: Partial<AppointmentFilters> = {
    actingOrganization: unlessEmpty(actingOrganization),
    status: filters.statuses?.length ? filters.statuses.map((status) => DOMAIN_STATUS[status]) : filters.status ? [DOMAIN_STATUS[filters.status]] : undefined,
    property: unlessEmpty(filters.property),
    serviceCategory: unlessEmpty(filters.serviceParent),
    vendorName: unlessEmpty(filters.vendorName),
    assigneeName: unlessEmpty(filters.assignee),
    workerName: unlessEmpty(filters.assigneeUser),
    search: unlessEmpty(filters.search?.trim()),
    searchVendor: filters.searchVendor,
    dateFrom: unlessEmpty(filters.dateFrom),
    dateTo: unlessEmpty(filters.dateTo),
  };
  return Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined));
}

export const toFilterOptions = ({ serviceCategories, vendorNames, workerNames }: ApiFilterOptions): AppointmentFilterOptions => ({
  serviceParents: serviceCategories,
  vendorNames,
  assigneeNames: workerNames,
});

export const API_PAGE_SIZE_LIMIT = 100;

export async function readAppointmentPage(
  list: (query: Partial<AppointmentListQuery>) => Promise<AppointmentPage>,
  query: Omit<Partial<AppointmentListQuery>, "page" | "pageSize">,
  page: number,
  pageSize: number,
): Promise<AppointmentPage> {
  if (pageSize <= API_PAGE_SIZE_LIMIT) return list({ ...query, page, pageSize });
  const offset = (page - 1) * pageSize;
  const firstApiPage = Math.floor(offset / API_PAGE_SIZE_LIMIT) + 1;
  const lastApiPage = Math.floor((offset + pageSize - 1) / API_PAGE_SIZE_LIMIT) + 1;
  const apiPages = await Promise.all(
    Array.from({ length: lastApiPage - firstApiPage + 1 }, (_, i) =>
      list({ ...query, page: firstApiPage + i, pageSize: API_PAGE_SIZE_LIMIT }),
    ),
  );
  const skipped = offset - (firstApiPage - 1) * API_PAGE_SIZE_LIMIT;
  return { items: apiPages.flatMap((apiPage) => apiPage.items).slice(skipped, skipped + pageSize), total: apiPages[0].total };
}

export async function readEveryAppointment(
  list: (query: Partial<AppointmentListQuery>) => Promise<AppointmentPage>,
  query: Omit<Partial<AppointmentListQuery>, "page" | "pageSize">,
): Promise<Appointment[]> {
  const first = await list({ ...query, page: 1, pageSize: API_PAGE_SIZE_LIMIT });
  const pageCount = Math.ceil(first.total / API_PAGE_SIZE_LIMIT);
  const rest = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, i) => list({ ...query, page: i + 2, pageSize: API_PAGE_SIZE_LIMIT })),
  );
  const items = [first, ...rest].flatMap((apiPage) => apiPage.items);
  return [...new Map(items.map((item) => [item.id, item])).values()];
}

const isWorkSnapshot = (snapshot: StoredWorkSnapshot): snapshot is PreviousWorkSnapshot =>
  Array.isArray(snapshot.tasks) && Array.isArray(snapshot.photos);

const workSnapshots = (stored: StoredWorkSnapshot[] | undefined): PreviousWorkSnapshot[] | undefined => {
  const snapshots = (stored ?? []).filter(isWorkSnapshot);
  return snapshots.length ? snapshots : undefined;
};

function metaData(record: Appointment): AppointmentMetaData {
  const meta: AppointmentMetaData = {
    work_started_at: record.workStartedAt ?? undefined,
    clock_in: storedClock(record.clockIn),
    clock_out: storedClock(record.clockOut),
    done_at: record.finishedAt ?? undefined,
    goback_at: record.goBackRequestedAt ?? undefined,
    cancelled_at: record.cancellation?.at ?? undefined,
    cancelled_by: record.cancellation?.by?.id,
    cancellation_notes: record.cancellation?.note ?? undefined,
    reassignment_requested_by: record.reassignmentRequest?.byUserId ?? undefined,
    reassignment_request_reason: record.reassignmentRequest?.reason,
    reassignment_requested_at: record.reassignmentRequest?.at ?? undefined,
    extra_photos: record.extraPhotos?.length
      ? record.extraPhotos.map(({ url, is_after, taken_at, user }) => ({ url, is_after, taken_at, user }))
      : undefined,
    previous_work: workSnapshots(record.previousWork),
  };
  return Object.fromEntries(Object.entries(meta).filter(([, value]) => value !== undefined)) as AppointmentMetaData;
}

export function toAppointmentJoin(record: Appointment): AppointmentJoin {
  const { subcontractor, worker } = record.assignment;
  return {
    id: record.id,
    property: {
      id: record.property.id,
      name: record.property.name,
      unit_number: record.unit?.number ?? "",
      beds: record.unit?.beds ?? 0,
      baths: record.unit?.baths ?? 0,
      image: record.property.image ?? undefined,
      address: record.property.address ?? undefined,
    },
    vendor: { id: record.vendor.id, name: record.vendor.name },
    service: { id: record.service.id, name: record.service.name, parent_name: record.service.category ?? "" },
    assigned_user: { id: worker?.id ?? null, name: worker?.name ?? null },
    assigned_organization: subcontractor ? { id: subcontractor.id, display_name: subcontractor.name } : null,
    created_at: record.createdAt,
    date: record.date,
    time: record.time ?? undefined,
    status: rowStatusOf(record.status),
    work_order_notes: record.workOrderNotes ?? undefined,
    ...(record.price === undefined ? {} : { final_price: record.price }),
    ...(record.pay.worker
      ? {
          individual_payment: record.pay.worker.amount,
          individual_payment_status: record.pay.worker.status as individual_payment_status | null,
        }
      : {}),
    ...(record.pay.subcontractor
      ? {
          subs_payment: record.pay.subcontractor.amount,
          subs_payment_status: record.pay.subcontractor.status as subs_payment_status | null,
        }
      : {}),
    cancelled_by_user: record.cancellation?.by ? { id: record.cancellation.by.id, name: record.cancellation.by.name } : null,
    meta_data: metaData(record),
    ...(record.billingException === undefined ? {} : { billing_exception: record.billingException }),
  };
}

export const withAnswerKeepingListedMoney = (listed: AppointmentJoin, answer: Appointment): AppointmentJoin => ({
  ...listed,
  ...toAppointmentJoin({ ...answer, price: undefined, pay: {} }),
});
